import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Btn, Input, Field } from "@/components/ui-kit";
import { formatINR, formatDate } from "@/lib/format";
import { Check, Loader2, CreditCard, History } from "lucide-react";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Billing() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    const [p, h] = await Promise.all([api.get("/owner/plans"), api.get("/payment/history")]);
    setPlans(p.data); setHistory(h.data);
  };
  useEffect(() => { load(); }, []);

  const buy = async (plan) => {
    setMsg(null);
    setLoadingPlan(plan.id);
    try {
      const { data } = await api.post("/payment/create-order", { plan_id: plan.id, coupon_code: coupon || null });

      if (data.mock) {
        // mock-mode: instantly verify with stub values (Razorpay keys not configured)
        const verify = await api.post("/payment/verify-payment", {
          razorpay_order_id: data.order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: "mock_signature",
          plan_id: plan.id,
        });
        await refresh();
        setMsg({ type: "ok", text: `Subscription activated (Test Mode): ${verify.data.plan_name} valid till ${formatDate(verify.data.expiry_date)}` });
        load();
        setLoadingPlan(null);
        return;
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setMsg({ type: "err", text: "Failed to load Razorpay checkout." });
        setLoadingPlan(null);
        return;
      }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "WebCabz",
        description: `${plan.name} subscription`,
        order_id: data.order_id,
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#FFB800" },
        handler: async (res) => {
          try {
            const verify = await api.post("/payment/verify-payment", { ...res, plan_id: plan.id });
            await refresh();
            await load();
            setMsg({ type: "ok", text: `Subscription activated: ${verify.data.plan_name}` });
          } catch (e) {
            setMsg({ type: "err", text: e.response?.data?.detail || "Verification failed" });
          }
        },
        modal: { ondismiss: () => setLoadingPlan(null) },
      });
      rzp.open();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed to start checkout" });
    }
    setLoadingPlan(null);
  };

  const tenant = user?.tenant;

  return (
    <div data-testid="billing-page">
      <PageHeader title="Subscription & Billing" subtitle="Upgrade your plan to unlock more vehicles, drivers, and bookings." />

      <div className="bg-asphalt text-white rounded-lg p-6 mb-6 grid sm:grid-cols-3 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-cab font-semibold">Current Plan</div>
          <div className="font-display font-bold text-2xl mt-1">{tenant?.plan_name || "Trial"}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-cab font-semibold">Status</div>
          <div className="font-display font-bold text-2xl mt-1 capitalize">{tenant?.status || "—"}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-cab font-semibold">Expires</div>
          <div className="font-display font-bold text-2xl mt-1">{formatDate(tenant?.expiry_date)}</div>
        </div>
      </div>

      {msg && (
        <div data-testid="billing-msg" className={`mb-6 px-4 py-3 rounded-md text-sm ${msg.type === "ok" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {msg.text}
        </div>
      )}

      <div className="mb-4 grid sm:grid-cols-3 gap-3">
        <Field label="Apply coupon">
          <Input data-testid="billing-coupon" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="e.g. LAUNCH50" />
        </Field>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {plans.map((p) => (
          <div key={p.id} data-testid={`billing-plan-${p.id}`} className={`bg-white rounded-lg p-6 ${p.is_popular ? "border-2 border-cab" : "border border-zinc-200"}`}>
            {p.is_popular && <div className="text-[11px] font-bold uppercase tracking-wider text-asphalt bg-cab inline-block px-2 py-0.5 rounded-full mb-3">Most Popular</div>}
            <div className="font-display font-bold text-2xl text-asphalt">{p.name}</div>
            <div className="text-sm text-zinc-500 mt-1">{p.description}</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display font-black text-4xl text-asphalt">{formatINR(p.price)}</span>
              <span className="text-sm text-zinc-500">/ {p.duration_days}d</span>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-zinc-700">
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> {p.vehicle_limit >= 9999 ? "Unlimited" : p.vehicle_limit} vehicles</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> {p.driver_limit >= 9999 ? "Unlimited" : p.driver_limit} drivers</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> {p.user_limit >= 9999 ? "Unlimited" : p.user_limit} team users</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> {p.booking_limit >= 999999 ? "Unlimited" : p.booking_limit.toLocaleString("en-IN")} bookings/mo</li>
            </ul>
            <Btn
              data-testid={`billing-buy-${p.id}`}
              variant={p.is_popular ? "primary" : "dark"}
              onClick={() => buy(p)}
              disabled={loadingPlan === p.id || tenant?.plan_id === p.id}
              className="w-full mt-6 justify-center"
            >
              {loadingPlan === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : tenant?.plan_id === p.id ? "Current Plan" : <><CreditCard className="h-4 w-4" /> Subscribe</>}
            </Btn>
          </div>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
          <History className="h-4 w-4 text-zinc-500" />
          <div className="font-display font-bold text-asphalt">Payment History</div>
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500">No payments yet.</div>
        ) : (
          <table className="tbl">
            <thead><tr><th>Plan</th><th>Amount</th><th>Payment ID</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {history.map((p) => (
                <tr key={p.id}>
                  <td className="font-medium">{p.plan_name}</td>
                  <td className="font-mono">{formatINR(p.amount)}</td>
                  <td className="font-mono text-xs">{p.payment_id}</td>
                  <td><span className="badge badge-success">{p.status}</span></td>
                  <td className="text-zinc-500">{formatDate(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
